import { Link } from "react-router-dom";
import { useEffect, type CSSProperties } from "react";
import { motion } from "motion/react";
import { MessageSquare, GitFork, Scale, Activity, ArrowUpRight } from "lucide-react";
import type { Strain } from "@/types";
import { MiniGraph } from "@/components/MiniGraph";
import { useStore } from "@/store/useStore";
import { Badge, TagChip } from "@/components/Badge";
import { Avatar } from "@/components/AppShell";
import { STRAIN_BADGE_META, timeAgo } from "@/lib/vocabulary";
import { cn } from "@/lib/cn";

const SOLID_BADGE_STYLE: Record<string, CSSProperties> = {
  info: { backgroundColor: "color-mix(in srgb, var(--color-define) 16%, var(--color-surface) 84%)", borderColor: "color-mix(in srgb, var(--color-define) 42%, var(--color-border) 58%)" },
  amber: { backgroundColor: "color-mix(in srgb, var(--color-undercut) 18%, var(--color-surface) 82%)", borderColor: "color-mix(in srgb, var(--color-undercut) 44%, var(--color-border) 56%)" },
  warn: { backgroundColor: "color-mix(in srgb, var(--color-secondary) 18%, var(--color-surface) 82%)", borderColor: "color-mix(in srgb, var(--color-secondary) 44%, var(--color-border) 56%)" },
  purple: { backgroundColor: "color-mix(in srgb, var(--color-reframe) 18%, var(--color-surface) 82%)", borderColor: "color-mix(in srgb, var(--color-reframe) 42%, var(--color-border) 58%)" },
  primary: { backgroundColor: "color-mix(in srgb, var(--color-primary) 16%, var(--color-surface) 84%)", borderColor: "color-mix(in srgb, var(--color-primary) 42%, var(--color-border) 58%)" },
  ok: { backgroundColor: "color-mix(in srgb, var(--color-support) 16%, var(--color-surface) 84%)", borderColor: "color-mix(in srgb, var(--color-support) 42%, var(--color-border) 58%)" },
  danger: { backgroundColor: "color-mix(in srgb, var(--color-danger) 16%, var(--color-surface) 84%)", borderColor: "color-mix(in srgb, var(--color-danger) 42%, var(--color-border) 58%)" },
};

export function StrainCard({ strain, index = 0 }: { strain: Strain; index?: number }) {
  const graph = useStore((s) => s.getGraph(strain.slug));
  const loadGraph = useStore((s) => s.loadGraph);
  const a = strain.activity;

  useEffect(() => {
    if (!graph) loadGraph(strain.slug).catch(() => undefined);
  }, [graph, loadGraph, strain.slug]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.4), duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/s/${strain.slug}`}
        className="group block rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden hover:border-[var(--color-primary)] transition-all hover:shadow-card hover:-translate-y-0.5"
      >
        {/* graph preview strip */}
        <div className="relative h-[130px] bg-grid border-b border-[var(--color-border)] overflow-hidden">
          {graph && (
            <div className="absolute inset-0 flex items-center justify-center">
              <MiniGraph graph={graph} width={320} height={130} />
            </div>
          )}
          <div className="absolute top-4 left-4 right-4 z-10 flex flex-wrap justify-start gap-1 pointer-events-none">
            {(strain.status_badges ?? []).map((b) => {
              const m = STRAIN_BADGE_META[b];
              return (
                <Badge
                  key={b}
                  tone={m.tone as any}
                  className="shadow-card"
                  style={SOLID_BADGE_STYLE[m.tone]}
                >
                  {m.label}
                </Badge>
              );
            })}
          </div>
          <div className="absolute right-4 bottom-4 z-10">
            <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-card text-[10px] font-mono text-[var(--color-ink-muted)]">
              {graph?.nodes.length ?? 0} nodes · {graph?.edges.length ?? 0} edges
            </div>
          </div>
        </div>

        {/* body */}
        <div className="p-4">
          <div className="flex items-start gap-2 mb-1.5">
            <h3 className="text-[15px] font-semibold text-[var(--color-ink)] leading-snug group-hover:text-[var(--color-primary)] transition-colors flex-1">
              {strain.title}
            </h3>
            <ArrowUpRight size={15} className="text-[var(--color-ink-faint)] group-hover:text-[var(--color-primary)] transition-colors shrink-0 mt-0.5" />
          </div>
          <p className="text-[13px] text-[var(--color-ink-muted)] leading-relaxed line-clamp-2 mb-3">
            {strain.summary}
          </p>

          {/* tags */}
          <div className="flex flex-wrap gap-1 mb-3">
            {strain.tags.slice(0, 4).map((t) => <TagChip key={t}>{t}</TagChip>)}
          </div>

          {/* metrics row */}
          <div className="flex items-center gap-3 text-[11px] font-mono text-[var(--color-ink-muted)]">
            {a && (
              <>
                <Metric icon={<MessageSquare size={11} />} value={a.comments} />
                <Metric icon={<GitFork size={11} />} value={a.forks} tone="primary" />
                <Metric icon={<Scale size={11} />} value={`${strain.contention_score ?? 0}`} tone="amber" />
                {a.mind_changes > 0 && (
                  <Metric icon={<Activity size={11} />} value={a.mind_changes} tone="inferred" />
                )}
              </>
            )}
            <span className="ml-auto text-[var(--color-ink-faint)]">{timeAgo(a?.last_activity ?? strain.updated_at)}</span>
          </div>

          {/* creator + crux */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--color-border)]">
            <Avatar initials={strain.creator_avatar ?? strain.creator.slice(0, 2).toUpperCase()} size={22} />
            <span className="text-[12px] text-[var(--color-ink-muted)]">{strain.creator}</span>
            {strain.top_crux_label && (
              <div className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[rgba(251,191,36,0.08)] border border-[rgba(251,191,36,0.2)] text-[10px] text-[var(--color-secondary)] font-mono">
                <Scale size={9} /> {strain.top_crux_label.length > 28 ? strain.top_crux_label.slice(0, 28) + "…" : strain.top_crux_label}
              </div>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function Metric({ icon, value, tone }: { icon: React.ReactNode; value: React.ReactNode; tone?: string }) {
  const color = tone === "primary" ? "text-[var(--color-primary)]"
    : tone === "amber" ? "text-[var(--color-secondary)]"
    : tone === "inferred" ? "text-[var(--color-inferred)]"
    : "text-[var(--color-ink-muted)]";
  return (
    <span className={cn("inline-flex items-center gap-0.5", color)}>
      {icon}{value}
    </span>
  );
}
